import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCheck } from 'react-icons/fa';
import { services, industries, works, partners, process, company } from '../data';
import { Container, Reveal, SectionHead, Eyebrow } from '../components/ui';
import { fetchIndex } from '../blog/api/contentApi';
import { contentPath, formatDate } from '../blog/content/contentTypes';
import useAsync from '../blog/useAsync';

const years = new Date().getFullYear() - company.founded;
const stats = [[`${years}+`, 'Years in industry'], ['100+', 'Projects delivered'], ['500+', 'Clients served'], [String(industries.length), 'Industries served']];

const Hero = ({ go }) => (
  <section className="relative bg-ink grid-bg text-white overflow-hidden pt-36 pb-20 md:pt-48 md:pb-28">
    <div className="absolute -top-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-secondary/50 blur-3xl" aria-hidden />
    <div className="absolute -bottom-40 right-0 w-[30rem] h-[30rem] rounded-full bg-primary/20 blur-3xl" aria-hidden />
    <Container className="relative grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#4cc3ff] mb-6">
          <span className="led w-2 h-2 rounded-full bg-[#4cc3ff]" /> Kolkata · Industrial automation since {company.founded}
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Keep your plant <span className="text-grad">running, controlled and connected.</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-6 text-lg md:text-xl text-white/70 max-w-xl">
          ACB servicing, VFD installation and commissioning, PLC and HMI programming, and digitization with industrial IoT, from one engineering team.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-9 flex flex-wrap gap-4">
          <button onClick={() => go('contact')} className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-[#0aa0ee] px-7 py-3.5 font-semibold transition">
            Request service <FaArrowRight />
          </button>
          <button onClick={() => go('services')} className="rounded-full border border-white/25 hover:bg-white/10 px-7 py-3.5 font-semibold transition">
            What we do
          </button>
        </motion.div>
      </div>

      {/* Decorative drive-output panel */}
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
        className="lg:col-span-5" aria-hidden>
        <div className="rounded-2xl bg-panel/90 border border-white/10 shadow-2xl shadow-black/40 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 font-mono text-[11px] uppercase tracking-widest text-white/50">
            <span>Drive output</span>
            <span className="flex items-center gap-2"><span className="led w-2 h-2 rounded-full bg-emerald-400" />Running</span>
          </div>
          <svg viewBox="0 0 400 180" className="w-full">
            <defs>
              <linearGradient id="g" x1="0" x2="1"><stop offset="0" stopColor="#8f89f0" /><stop offset="1" stopColor="#4cc3ff" /></linearGradient>
            </defs>
            {[30, 90, 150].map((y) => <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="#fff" strokeOpacity=".07" />)}
            <path d="M0 90 Q 25 20 50 90 T 100 90 T 150 90 T 200 90 T 250 90 T 300 90 T 350 90 T 400 90" fill="none" stroke="url(#g)" strokeWidth="2.5" className="wave-flow" />
            <path d="M0 90 Q 25 20 50 90 T 100 90 T 150 90 T 200 90 T 250 90 T 300 90 T 350 90 T 400 90" fill="none" stroke="#fff" strokeOpacity=".08" strokeWidth="2.5" />
          </svg>
          <div className="grid grid-cols-3 border-t border-white/10 text-center">
            {[['Freq', '50.0 Hz'], ['Current', '12.4 A'], ['Status', 'OK']].map(([k, v]) => (
              <div key={k} className="py-4 border-r last:border-r-0 border-white/10">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">{k}</p>
                <p className="font-display font-semibold text-lg mt-1">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </Container>
  </section>
);

const Stats = () => (
  <section className="bg-secondary text-white">
    <Container className="grid grid-cols-2 md:grid-cols-4 divide-white/15 md:divide-x gap-y-8 py-12">
      {stats.map(([n, l]) => (
        <div key={l} className="md:px-8 first:md:pl-0">
          <p className="font-display font-bold text-4xl md:text-5xl">{n}</p>
          <p className="text-sm uppercase tracking-wider text-white/70 mt-2">{l}</p>
        </div>
      ))}
    </Container>
  </section>
);

const Services = ({ go }) => (
  <section id="services" className="py-20 md:py-28 bg-white scroll-mt-24">
    <Container>
      <Reveal><SectionHead eyebrow="What we do" title="Four disciplines. One accountable team."
        text="From the breaker in the panel to the dashboard on your phone, we install, tune, maintain and connect it." /></Reveal>
      <div className="mt-14 space-y-6">
        {services.map((s, i) => (
          <Reveal key={s.id}>
            <article className={`group grid md:grid-cols-12 rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl hover:shadow-secondary/10 hover:border-primary/40 transition`}>
              <div className={`md:col-span-5 ${i % 2 ? 'md:order-2' : ''} ${s.contain ? 'bg-mist p-6' : ''} h-56 md:h-auto min-h-[16rem]`}>
                <img src={s.image} alt={s.title} loading="lazy"
                  className={`w-full h-full ${s.contain ? 'object-contain' : 'object-cover'}`} />
              </div>
              <div className="md:col-span-7 p-7 md:p-10 flex flex-col justify-center">
                <p className="font-mono text-sm text-primary">{s.n} / {s.short}</p>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-secondary mt-2">{s.title}</h3>
                <p className="mt-3 text-slate-600">{s.text}</p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-slate-700"><FaCheck className="mt-1 text-primary shrink-0" size={11} />{p}</li>
                  ))}
                </ul>
                <button onClick={() => go('contact')} className="mt-6 self-start inline-flex items-center gap-2 font-semibold text-secondary hover:text-primary transition">
                  Enquire about this <FaArrowRight className="group-hover:translate-x-1 transition" size={12} />
                </button>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const Process = () => (
  <section className="py-20 md:py-24 bg-mist">
    <Container>
      <Reveal><SectionHead eyebrow="How we work" title="From site survey to support." /></Reveal>
      <ol className="mt-12 grid md:grid-cols-5 gap-4">
        {process.map((p, i) => (
          <Reveal key={p.t} delay={i * 0.06}>
            <li className="relative h-full rounded-xl bg-white border border-slate-200 p-5">
              <span className="font-mono text-xs text-primary">STEP {i + 1}</span>
              <h3 className="font-display font-semibold text-xl text-secondary mt-1">{p.t}</h3>
              <p className="text-sm text-slate-600 mt-2">{p.d}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </Container>
  </section>
);

const Industries = () => (
  <section id="industries" className="py-20 md:py-24 bg-white scroll-mt-24">
    <Container className="grid lg:grid-cols-5 gap-10 items-start">
      <Reveal className="lg:col-span-2"><SectionHead eyebrow="Industries" title="Built for heavy, continuous duty."
        text="Our work spans process plants, utilities and critical buildings." /></Reveal>
      <Reveal delay={0.1} className="lg:col-span-3">
        <ul className="flex flex-wrap gap-3">
          {industries.map((n) => (
            <li key={n} className="rounded-full border border-secondary/20 bg-mist px-5 py-2.5 font-medium text-secondary hover:bg-secondary hover:text-white transition">{n}</li>
          ))}
        </ul>
      </Reveal>
    </Container>
  </section>
);

const Work = () => (
  <section className="py-20 md:py-28 bg-ink text-white grid-bg">
    <Container>
      <Reveal><SectionHead light eyebrow="Recent work" title="Projects we've delivered." /></Reveal>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {works.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.08}>
            <article className="h-full rounded-2xl overflow-hidden bg-panel border border-white/10 hover:border-primary/60 transition">
              <img src={w.image} alt={w.title} loading="lazy" className="w-full h-52 object-cover" />
              <div className="p-6">
                <span className="font-mono text-[11px] uppercase tracking-widest text-[#4cc3ff]">{w.tag}</span>
                <h3 className="font-display font-semibold text-xl mt-2">{w.title}</h3>
                <p className="text-white/65 mt-2 text-sm">{w.text}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const Partners = () => (
  <section className="py-16 bg-white border-b border-slate-200">
    <Container className="flex flex-col md:flex-row md:items-center gap-8 md:gap-16">
      <Eyebrow>Technology partners</Eyebrow>
      <div className="flex flex-wrap items-center gap-10 md:gap-16">
        {partners.map((p) => (
          <img key={p.name} src={p.image} alt={p.name} loading="lazy" className="h-10 md:h-12 w-auto object-contain" />
        ))}
      </div>
    </Container>
  </section>
);

const LatestInsights = () => {
  const { data } = useAsync(fetchIndex, []);
  // Silent when the content server is unreachable or has nothing published: the rest of the page is unaffected.
  const posts = (data || []).slice(0, 3);
  if (!posts.length) return null;
  return (
    <section className="py-20 md:py-24 bg-mist">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Reveal><SectionHead eyebrow="Insights" title="Latest articles" text="Guides, case studies and news from our engineering team." /></Reveal>
          <Link to="/insights" className="inline-flex items-center gap-2 font-semibold text-secondary hover:text-primary transition">All insights <FaArrowRight size={12} /></Link>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <Link to={contentPath(p)} className="block h-full rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-xl hover:shadow-secondary/10 hover:border-primary/40 transition">
                <span className="font-mono text-[11px] uppercase tracking-widest text-primary">{p.categoryName || 'Article'}</span>
                <h3 className="font-display font-semibold text-xl text-secondary mt-2">{p.title}</h3>
                {p.excerpt && <p className="text-slate-600 text-sm mt-2 line-clamp-3">{p.excerpt}</p>}
                <p className="text-xs text-slate-500 mt-4">{[formatDate(p.sourceModified || p.importedAt), `${p.readingTimeMinutes || 1} min read`].filter(Boolean).join(' · ')}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

const Home = ({ go }) => (
  <>
    <Hero go={go} />
    <Stats />
    <Services go={go} />
    <Process />
    <Industries />
    <Work />
    <LatestInsights />
    <Partners />
  </>
);

export default Home;
